import { bigIntToVmNumber, binToHex, CashAddressType, cashAddressTypeBitsToType, cashAssemblyToBin, decodeCashAddressFormat, decodeCashAddressFormatWithoutPrefix, decodeCashAddressVersionByte, encodeTransactionBch, lockingBytecodeToCashAddress, sha256, hash256, swapEndianness, binToFixedLength, bigIntToBinUintLE, binToBigIntUintLE, hexToBin, } from '@bitauth/libauth';
export function getScriptHash(lockingBytecode, reversed = true) {
    let hashHex = binToHex(sha256.hash(lockingBytecode));
    if (reversed)
        return swapEndianness(hashHex);
    return hashHex;
}
export function getHeaderTimestamp(header) {
    if (typeof header == "string")
        header = hexToBin(header);
    return binToBigIntUintLE(header.slice(4 + 64, 4 + 64 + 4));
}
export function getAddress(lockingBytecode, prefix = "bitcoincash", tokenSupport = false) {
    const result = lockingBytecodeToCashAddress({ prefix: prefix, bytecode: lockingBytecode, tokenSupport: tokenSupport });
    if (typeof result === 'string')
        throw (result);
    return result.address;
}
export function getTransactionId(txn) {
    return swapEndianness(binToHex(hash256(txn)));
}
export const binToBigIntUint256LE = (value) => {
    const uint256Bytes = 32;
    return binToBigIntUintLE(binToFixedLength(value, uint256Bytes), uint256Bytes);
};
export const bigIntToBinUint256LE = (value) => {
    const uint256Bytes = 32;
    return binToFixedLength(bigIntToBinUintLE(value), uint256Bytes);
};
export function isTokenaddr(address) {
    let result;
    // If the address has a prefix decode it as is
    if (address.includes(":")) {
        result = decodeCashAddressFormat(address);
    }
    else {
        // otherwise, derive the network from the address without prefix
        result = decodeCashAddressFormatWithoutPrefix(address);
    }
    if (typeof result === "string")
        throw new Error(result);
    const info = decodeCashAddressVersionByte(result.version);
    if (typeof info === "string")
        throw new Error(info);
    const type = cashAddressTypeBitsToType[info.typeBits];
    if (type === undefined) {
        throw Error("Wrong cashaddress type");
    }
    return ([CashAddressType.p2pkhWithTokens, CashAddressType.p2shWithTokens].indexOf(type) !== -1);
}
export function checkTokenaddr(cashaddr, enforce) {
    if (enforce && !isTokenaddr(cashaddr)) {
        throw new Error("Error trying to send to a non-tokenaware cash address");
    }
}
export function sumUtxoValue(utxos, subTokenDust = false) {
    if (utxos.length > 0) {
        const balanceArray = utxos.map((o) => {
            return o.value;
        });
        const balance = balanceArray.reduce((a, b) => a + b - (subTokenDust ? 800 : 0), 0);
        return balance;
    }
    else {
        return 0;
    }
}
export function sumTokenAmounts(utxos, tokenId) {
    if (tokenId && typeof tokenId !== "string")
        tokenId = binToHex(tokenId);
    if (utxos.length > 0) {
        const tokenArray = utxos
            .filter((utxo) => utxo.token_data?.category === tokenId)
            .map((o) => {
            return o.token_data?.amount || 0n;
        });
        const balance = tokenArray.reduce((a, b) => BigInt(a) + BigInt(b), 0n);
        return BigInt(balance);
    }
    else {
        return 0n;
    }
}
export function valueDifference(source, outputs) {
    let sumOut = sumOutputValue(outputs);
    let sumIn = sumSourceOutputValue(source);
    return sumOut - sumIn;
}
export function sumSourceOutputValue(source, subTokenDust = false) {
    if (source.length > 0) {
        const balanceArray = source.map((o) => {
            return o.valueSatoshis;
        });
        const balance = balanceArray.reduce((a, b) => a + b - (subTokenDust ? 800n : 0n), 0n);
        return balance;
    }
    else {
        return 0n;
    }
}
export function sumOutputValue(outputs, subTokenDust = false) {
    if (outputs.length > 0) {
        const balanceArray = outputs.map((o) => {
            return o.valueSatoshis;
        });
        const balance = balanceArray.reduce((a, b) => a + b - (subTokenDust ? 800n : 0n), 0n);
        return balance;
    }
    else {
        return 0n;
    }
}
export function getDistinctFungibleTokenCategories(source, outputs) {
    let inArray = source
        .filter((o) => o.token)
        .map((o) => {
        return o.token?.category;
    }).filter(cat => cat !== undefined);
    let outputArray = outputs
        .filter((o) => o.token !== undefined)
        .map((o) => {
        return o.token?.category;
    }).filter(cat => cat !== undefined);
    // make distinct
    return [...new Set([...inArray, ...outputArray])];
}
export function tokenDifference(source, outputs, asset) {
    let sumTokenAmountsOut = sumOutputTokenAmounts(outputs, asset);
    let sumTokenAmountsIn = sumSourceOutputTokenAmounts(source, asset);
    return sumTokenAmountsOut - sumTokenAmountsIn;
}
export function sumOutputTokenAmounts(outputs, tokenId) {
    if (tokenId && typeof tokenId !== "string")
        tokenId = binToHex(tokenId);
    if (outputs.length > 0) {
        const tokenArray = outputs
            .filter((o) => o.token && binToHex(o.token?.category) === tokenId)
            .map((o) => {
            return BigInt(o.token?.amount) || 0n;
        });
        const balance = tokenArray.reduce((a, b) => BigInt(a) + BigInt(b), 0n);
        return BigInt(balance);
    }
    else {
        return 0n;
    }
}
export function sumSourceOutputTokenAmounts(source, tokenId) {
    if (tokenId && typeof tokenId !== "string")
        tokenId = binToHex(tokenId);
    if (source.length > 0) {
        const tokenArray = source
            .filter((source) => source.token && binToHex(source.token?.category) === tokenId)
            .map((o) => {
            return BigInt(o.token?.amount) || 0n;
        });
        const balance = tokenArray.reduce((a, b) => BigInt(a) + BigInt(b), 0n);
        return BigInt(balance);
    }
    else {
        return 0n;
    }
}
export function getTransactionFees(tx, rate = 1) {
    return BigInt(encodeTransactionBch(tx).length * rate);
}
export function checkForEmptySeed(seed) {
    let blankSeed = "4ed8d4b17698ddeaa1f1559f152f87b5d472f725ca86d341bd0276f1b61197e21dd5a391f9f5ed7340ff4d4513aab9cce44f9497a5e7ed85fd818876b6eb402e";
    let seedBin = new Uint8Array(seed);
    if (blankSeed == binToHex(seedBin))
        throw Error("Seed was generated using empty mnemonic");
}
export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
export const numToVm = (n) => bigIntToVmNumber(BigInt(n));
export function cashAssemblyToHex(str) {
    let result = cashAssemblyToBin(str);
    if (typeof result === "string")
        throw result;
    return binToHex(result);
}
export function catUint8(uint8arrays) {
    // Determine the length of the result.
    const totalLength = uint8arrays.reduce((total, uint8array) => total + uint8array.byteLength, 0);
    // Allocate the result.
    const result = new Uint8Array(totalLength);
    // Copy each Uint8Array into the result.
    let offset = 0;
    uint8arrays.forEach((uint8array) => {
        result.set(uint8array, offset);
        offset += uint8array.byteLength;
    });
    return result;
}
//# sourceMappingURL=util.js.map